import React, { FC, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

interface CreateInviteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (email: string) => Promise<void>;
}

interface CreateInviteFormData {
  email: string;
}

export const CreateInviteDialog: FC<CreateInviteDialogProps> = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: CreateInviteFormData = useMemo(() => {
    return {
      email: '',
    };
  }, []);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      email: Yup.string()
        .email('Please enter a valid email address')
        .required('Email address is required'),
    });
  }, []);

  const handleSubmit = async (values: CreateInviteFormData) => {
    setIsSubmitting(true);
    try {
      await onSave(values.email);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogContent>
        <Formik
          validateOnMount
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Typography variant="MHHeading" mb={1}>
                Create Invite
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enter the email address of the person that should receive an invite for this
                subscription.
              </Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder="example@email.com"
                    data-cy="invite-email-input"
                  />
                </Grid>
              </Grid>
              <Grid container direction="row" justifyContent="flex-end">
                <Grid item>
                  <Button
                    variant="brandContained"
                    color="brandGrey"
                    onClick={onClose}
                    disabled={isSubmitting}
                    sx={{ mr: 1 }}
                    data-cy="cancel-invite-button">
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="brandContained"
                    color="brandBlack"
                    startIcon={isSubmitting ? <CircularProgress size="1em" /> : undefined}
                    disabled={isSubmitting || !touched.email || !!Object.keys(errors).length}
                    data-cy="confirm-invite-button">
                    Send Invite
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
